/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-nested-ternary */
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import ProgressBar from '@brands/Components/ProgressBar/ProgressBar';
import { UploadedFile, uploadMediaToCase } from '@brands/Dashboard/MyProfile/Patient/utils/utilsFunctions';
import Camera from '@brands/Dashboard/PatientForms/PatientForm/ImageUpload/Components/Camera';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { addAdditionalInfo } from '@brands/services/cases/addAdditionalInfo';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { ICase } from '@brands/services/cases/types/ICase';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectPatient } from '@brands/store/selectors/selectedPatient';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import { displayErrorDetails } from '@brands/Utils/displayError';
import {
  getPatientReviewProfileRoleSegment,
  getStaffReviewProfileBasePath,
  isStaffCaseCreationRole,
} from '@brands/Utils/reviewProfileBasePath';
import { truncateFilename } from '@brands/Utils/truncateString';
import { CASE_ATTACHMENT_ACCEPT, isAcceptedCaseAttachmentFile } from '@brands/Utils/caseAttachmentFiles';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@mui/material';
import { heicTo } from 'heic-to';
import { uniqBy } from 'lodash';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IoClose } from 'react-icons/io5';
import Modal, { Styles } from 'react-modal';
import { useBeforeUnload, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import closeICon from '../../Patient/components/closeIconSVG.svg';
import UnsavedChangesModal from '../UnsavedChangesModal/UnsavedChangesModal';
import styles from './styles.module.scss';
import uploadIcon from './utils/images/uploadIconSVG.svg';
import { validationSchema } from './utils/validationSchema';

export interface AddAdditionalInformationFormSubmit {
  notes: string;
  images: string[];
  caseId: string;
}

interface AdditionalInformationModalProps {
  isOpen: boolean;
  toggleModal: (value: boolean) => void;
  caseId: string;
}

const mobileCustomStylesDashboard: Styles = {
  ...customStylesDashboard,
  overlay: {
    ...(customStylesDashboard?.overlay || {}),
    overflowY: 'scroll' as const,
    overflowX: 'hidden' as const,
  },
  content: {
    ...(customStylesDashboard?.content || {}),
    width: '100vw',
    height: '100vh',
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    right: '0',
    bottom: '0',
    padding: '90px 16px 32px 16px',
    margin: '0',
    borderRadius: '0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
};

const customStylesDashboardDesktop: Styles = {
  ...customStylesDashboard,
  overlay: {
    ...customStylesDashboard.overlay,
  },
  content: {
    ...customStylesDashboard.content,
    flexDirection: 'column',
    width: '444px',
    padding: '55px 32px 32px',
    overflowY: 'auto',
    maxHeight: '850px',
    justifyContent: 'flex-start',
  },
};
const AdditionalInformationModal: FC<AdditionalInformationModalProps> = ({ isOpen, toggleModal, caseId }) => {
  useBeforeUnload((event) => {
    if (hasUnsavedChanges) {
      event.preventDefault();
      setHasUnsavedChangesModal(true);
    }
  });

  const { userInfo } = useAppSelector(selectAuth);
  const { selectedPatient } = useAppSelector(selectPatient);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  const triggerSubmit = (): void => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  const handleButtonClick = (): void => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const closeModal = (): void => {
    if (hasUnsavedChanges) {
      setHasUnsavedChangesModal(true);
    } else {
      if (
        userInfo.role.name !== 'Patient' &&
        selectedPatient?.id != null &&
        isStaffCaseCreationRole(userInfo.role.name)
      ) {
        navigate(
          `${getStaffReviewProfileBasePath(userInfo.role.name)}/${getPatientReviewProfileRoleSegment(
            selectedPatient,
          )}/${selectedPatient.id}`,
        );
      }
      toggleModal(false);
    }
  };

  const screenSize = useWindowSize();
  const navigate = useNavigate();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasUnsavedChangesModal, setHasUnsavedChangesModal] = useState(false);
  const isMobile = useMemo(() => screenSize.width < 768, [screenSize.width]);
  const routeCaseId = caseId as string;

  const [openCamera, setOpenCamera] = useState(false);
  const [images, setImages] = useState<UploadedFile[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [thisCase, setCase] = useState<ICase>();
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);

  const onSubmit = async (data: AddAdditionalInformationFormSubmit): Promise<void> => {
    if (hasUnsavedChangesModal) setHasUnsavedChangesModal(false);
    const numericCaseId = Number(routeCaseId);
    if (!routeCaseId || Number.isNaN(numericCaseId)) {
      displayErrorDetails(new Error('Unable to submit: no case is selected.'));
      return;
    }
    setLoading(true);
    try {
      if (data.notes) {
        await addAdditionalInfo(numericCaseId, { additional_info: data.notes }, userInfo.role.name);
      }
      if (images.length > 0) {
        // thisCase is fetched on mount and can still be unresolved if the
        // patient attaches an image and submits before that GET lands —
        // uploadMediaToCase can't recover from an undefined case (no id to
        // look up a one_report from), so fetch fresh here rather than trust
        // the possibly-stale state.
        const caseForUpload = thisCase ?? (await getOneCase(numericCaseId));
        await uploadMediaToCase(caseForUpload, images);
      }
    } catch (error) {
      displayErrorDetails(error);
    } finally {
      toggleModal(false);
      setLoading(false);
    }
  };

  const onInvalid = (errors: any): void => {
    const error = Object.values(errors)[0] as any;
    if (error) {
      toast.error(Object.values(errors).length > 1 ? 'Please fill in all fields' : error.message, {
        className: 'fs-unmask',
      });
    }
  };

  const { register, handleSubmit, setValue } = useForm<AddAdditionalInformationFormSubmit>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const onCloseCamera = (): void => {
    setOpenCamera(false);
  };

  const onPhotoTaken = (uri: string): void => {
    setImages([...images, { dataURL: uri, name: 'Capture.png', mimetype: 'image/png' }]);
  };

  const MAX_FILE_SIZE_MB = 3;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  const MAX_FILES = 10;

  const isHeicFile = (file: File): boolean => /\.(heic|heif)$/i.test(file.name) || /^image\/hei[cf]$/i.test(file.type);

  const readAsDataURL = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const addImages = async (files: File[]): Promise<void> => {
    const validFiles = files.filter((file) => isAcceptedCaseAttachmentFile(file) && file.size <= MAX_FILE_SIZE_BYTES);

    if (images.length + validFiles.length > MAX_FILES) {
      toast.error(`You can only upload up to ${MAX_FILES} files.`);
      return;
    }

    const imagesArray: UploadedFile[] = await Promise.all(
      validFiles.map(async (file) => {
        if (isHeicFile(file)) {
          const jpegBlob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.8 });
          const dataURL = await readAsDataURL(jpegBlob as Blob);
          return { name: file.name.replace(/\.(heic|heif)$/i, '.jpg'), dataURL, mimetype: 'image/jpeg' };
        }
        const dataURL = await readAsDataURL(file);
        return { name: file.name, dataURL, mimetype: file.type || undefined };
      }),
    );

    setImages(uniqBy([...images, ...imagesArray], 'dataURL'));

    const rejectedFiles = files.filter((file) => file.size > MAX_FILE_SIZE_BYTES);
    if (rejectedFiles.length > 0) {
      toast.error(`Some files exceed the ${MAX_FILE_SIZE_MB}MB limit and were not uploaded.`);
    }
  };

  // Drag and Drop Event Handlers
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    void addImages(files);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(event.target.files as unknown as File[]);
    void addImages(files);
  };

  const deleteMedia = (mediaName: string): void => {
    const selectedMedia = images.find((i) => i.dataURL === mediaName);
    if (selectedMedia) {
      const idx = images.indexOf(selectedMedia);
      setImages(images.filter((_, index) => index !== idx));
    }
  };

  useEffect(() => {
    if (!routeCaseId) return;
    (async () => {
      try {
        const res = await getOneCase(Number(routeCaseId));
        setCase(res);
      } catch (error) {
        displayErrorDetails(error);
      }
    })();
  }, [routeCaseId]);

  useEffect(() => {
    setHasUnsavedChanges(!!notes || images.length > 0);
  }, [notes, images]);

  useEffect(() => {
    if (notes || images.length > 0) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [notes, images]);

  //   if (loading || !thisCase) {
  //     return <Loading dotted fullScreen />;
  //   }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      style={isMobile ? mobileCustomStylesDashboard : customStylesDashboardDesktop}
      contentLabel="Modal"
      ariaHideApp={false}
    >
      {isMobile ? (
        <div className={styles.backDiv}>
          <ProgressBar currentStep={1} totalSteps={1} onBack={closeModal} hideStepNumbers />
        </div>
      ) : (
        <div className={styles.closeICon}>
          <img src={closeICon} onClick={() => closeModal()} alt="close" />
        </div>
      )}
      <div className={styles.addAdditionalInformationContainer}>
        <div className={styles.addAdditionalInformationHeader}>
          <h2 className={`fs-unmask ${styles.addAdditionalInformationMainTitle}`}>Additional information</h2>
        </div>
        <form ref={formRef} className={styles.formContainer} onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <div className={styles.textAreaWrapper}>
            <TextField
              {...register('notes')}
              label="notes for provider"
              name="notes"
              onChange={(e) => {
                setValue('notes', e.target.value);
                setNotes(e.target.value);
              }}
              onBlur={(e) => {
                setValue('notes', e.target.value.trim());
                setNotes(e.target.value.trim());
              }}
              multiline
              minRows={3}
              maxRows={6}
              inputClass={`${styles.profileInputClass} fs-exclude`}
              wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
            />
          </div>
          <div className={styles.addAdditionalInformationImages}>
            <div className={`fs-unmask ${styles.addAdditionalInformationTitle}`}>
              Upload images or documents for provider
            </div>
            <div className={styles.draggingArea} onDragOver={handleDragOver} onDrop={handleDrop}>
              <div className={styles.draggingAreaHeader}>
                <div className={`fs-unmask ${styles.dragTextContainer}`}>
                  <div className={styles.uploadImageWrap}>
                    <section className={styles.uploadSection}>
                      <label htmlFor="formId" className={styles.uploadImageLabel}>
                        <input
                          {...register('images')}
                          ref={(el) => {
                            fileInputRef.current = el;
                            register('images').ref(el);
                          }}
                          type="file"
                          id="formId"
                          onChange={handleChange}
                          multiple
                          hidden
                          accept={CASE_ATTACHMENT_ACCEPT}
                        />
                        <div className={styles.uploadTextRow}>
                          <span className={styles.selectFilesText}>Select files to upload</span>
                          <span className={styles.dragText}>&nbsp;or drag and drop</span>
                        </div>
                      </label>
                    </section>

                    <div className={styles.addAdditionalInformationSelectFiles}>
                      <Button variant="outlined" onClick={handleButtonClick}>
                        <img src={uploadIcon} />
                        Select Files
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              {images.length > 0 && (
                <div className={styles.grid}>
                  {images.map((file) => {
                    const isImage = /^data:image/i.test(file.dataURL);
                    return (
                      <div className={`fs-exclude ${styles.imageWrap}`} key={file.dataURL}>
                        <span className={styles.removeImage} onClick={() => deleteMedia(file.dataURL)}>
                          <IoClose />
                        </span>
                        {isImage ? (
                          <img className="fs-exclude" src={file.dataURL} alt={file.name} />
                        ) : (
                          <p className="fs-exclude">{truncateFilename(file.name, 20)}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className={styles.AddAdditionalInformationButtonDiv}>
            <Button
              type="submit"
              variant="contained"
              className={`fs-unmask ${styles.addAdditionalInformationSubmitButton}`}
              disabled={loading || disabled}
            >
              Submit
            </Button>
            <Button variant="text" className={`fs-unmask ${styles.cancel}`} onClick={() => closeModal()}>
              Cancel
            </Button>
          </div>
        </form>
        {hasUnsavedChangesModal && (
          <UnsavedChangesModal
            isOpen={hasUnsavedChangesModal}
            closeParentModal={toggleModal}
            toggleModal={setHasUnsavedChangesModal}
            saveChange={triggerSubmit}
            loading={loading}
            hideModal={() => setHasUnsavedChangesModal(false)}
          />
        )}
        {openCamera && <Camera onClose={onCloseCamera} onPhotoTaken={onPhotoTaken} />}
      </div>
    </Modal>
  );
};

Modal.setAppElement('#root');

export default AdditionalInformationModal;
